import * as Yup from "yup";

import {
    CurrencyPicker,
    OfficeButton,
    PDFRenderer,
    Show,
    TextInput,
} from "components";
import {
    ReportOnQuery,
    ReportOnQueryVariables,
} from "_graphql/queries/__generated__/ReportOnQuery";

import config from "config";
import { generateReport } from "_graphql/visal/queries/reports";
import toast from "react-hot-toast";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { wrapClick } from "utils";

const initialValues = {
    currency: "",
    from: "",
    to: "",
};

const validationSchema = Yup.object().shape({
    showAdvancedFilters: Yup.boolean().notRequired(),
    currency: Yup.string().required("Currency is required"),
    from: Yup.string().required("From date is required"),
    to: Yup.string().required("To date is required"),
});

const ReportingTab = () => {
    const [generate, { loading, data }] = useMutation<
        ReportOnQuery,
        ReportOnQueryVariables
    >(generateReport, {
        onCompleted: (data) => {},
        onError: (error) => {
            console.error("Error generating report:", error);
            toast.error("Error generating report");
        },
    });

    const form = useFormik<any>({
        initialValues,
        validationSchema,
        onSubmit: () => {
            form.setFormikState((state) => ({ ...state, status: "SUBMITTED" }));
            form.setSubmitting(false);
            return;
        },
    });

    useEffect(() => {
        form.setFormikState((state) => ({ ...state, status: "NOT_SUBMITTED" }));
        form.setSubmitting(false);
    }, [form.values.currency, form.values.date, form.values.cheque]);

    return (
        <div className="w-full">
            <div className="py-3 flex justify-between">
                <div>
                    <h1 className="font-bold text-primary text-lg">
                        Reporting
                    </h1>
                </div>
                <div className="flex items-center space-x-2">
                    <OfficeButton
                        disabled={loading}
                        onClick={wrapClick(form.handleSubmit)}>
                        {loading ? "Generating ..." : "Generate Report"}
                    </OfficeButton>
                </div>
            </div>
            <form className="space-y-6 divide-y shadow-sm rounded-md bg-white p-6 divide-gray-200">
                <div>
                    <span className="text-xs font-light">
                        Reporting Parameters
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="col-span-2">
                            <CurrencyPicker
                                id={"currency"}
                                placeholder="select currency category"
                                label={"Currency"}
                                {...form}
                            />
                        </div>
                    </div>
                </div>

                <div className="pt-6">
                    <span className="text-xs font-light">Query Date Range</span>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <TextInput
                                id="from"
                                label={
                                    form.values.queryOnPayment
                                        ? "Payment from"
                                        : "From"
                                }
                                type="date"
                                placeholder=""
                                required={true}
                                {...form}
                            />
                        </div>
                        <div>
                            <TextInput
                                id="to"
                                label={
                                    form.values.queryOnPayment
                                        ? "Payment to"
                                        : "To"
                                }
                                type="date"
                                placeholder=""
                                required={true}
                                {...form}
                            />
                        </div>
                    </div>
                </div>
            </form>

            <Show if={Boolean(form.status === "SUBMITTED" && form.isValid)}>
                <PDFRenderer
                    url={`${config.asset.uri}excel_monthly_brokerage/${btoa(
                        JSON.stringify({
                            from: form?.values?.from,
                            to: form?.values?.to,
                        })
                    )}`}
                />
            </Show>
        </div>
    );
};

export default ReportingTab;

const getOfferStatuses = (selected: string[]) => {
    // if the selected array contains all, don't show the rest
    if (selected.includes("ALL")) {
        return [{ label: "ALL", value: "ALL" }];
    }
    // if the selected array contains open, don't show the all as well
    if (
        selected.includes("OPEN") ||
        selected.includes("PENDING") ||
        selected.includes("CLOSED")
    ) {
        return [
            { label: "OPEN", value: "OPEN" },
            { label: "PENDING", value: "PENDING" },
            { label: "CLOSED", value: "CLOSED" },
        ];
    }
    return [
        { label: "ALL", value: "ALL" },
        { label: "OPEN", value: "OPEN" },
        { label: "PENDING", value: "PENDING" },
        { label: "CLOSED", value: "CLOSED" },
    ];
};
